/**
 * Ad-campaign attribution (UTM). Captures utm_* from the landing URL (first
 * touch, 30-day window), keeps them across pages, and lets sign-up links and
 * the sign-up event carry them so ad visitors who register are counted.
 * Never stores personal data — only campaign labels.
 */

const KEY = "statlign_utm_v1";
const WINDOW_MS = 30 * 24 * 60 * 60 * 1000;
export const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;

export type Utm = Partial<Record<(typeof UTM_KEYS)[number], string>> & { at?: number };

function clean(v: string | null): string | undefined {
  if (!v) return undefined;
  const s = v.replace(/[^\w\-.~ ]/g, "").trim().slice(0, 80);
  return s || undefined;
}

/** Read utm_* from a query string. */
export function parseUtm(search: string): Utm {
  const p = new URLSearchParams(search);
  const out: Utm = {};
  for (const k of UTM_KEYS) {
    const v = clean(p.get(k));
    if (v) out[k] = v;
  }
  return out;
}

/** Call on each navigation; a new campaign landing replaces the stored one. */
export function captureUtm(search?: string) {
  if (typeof window === "undefined") return;
  const found = parseUtm(search ?? window.location.search);
  if (!found.utm_source && !found.utm_campaign) return;
  try {
    localStorage.setItem(KEY, JSON.stringify({ ...found, at: Date.now() }));
  } catch {
    /* private mode */
  }
}

export function getUtm(): Utm | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const u = JSON.parse(raw) as Utm;
    if (!u?.at || Date.now() - u.at > WINDOW_MS) return null;
    return u;
  } catch {
    return null;
  }
}

/** Append stored (or default) UTMs to an internal link such as /auth. */
export function withUtm(path: string, fallback?: Utm): string {
  const u = getUtm() ?? fallback;
  if (!u) return path;
  const [base, qs = ""] = path.split("?");
  const p = new URLSearchParams(qs);
  for (const k of UTM_KEYS) if (u[k] && !p.has(k)) p.set(k, u[k]!);
  const q = p.toString();
  return q ? `${base}?${q}` : base!;
}
